import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../constants/theme';
import UnifiedPageHeader from '../components/UnifiedPageHeader';
import PageContainer from '../components/layout/PageContainer';
import { 
  mockMarketIntelligence, 
  getSentimentColor, 
  getDifficultyColor, 
  formatNumber 
} from '../utils/mockMarketIntelligence';
import { hasPermission } from '../utils/roleBasedAccess';

const ContentArea = styled.div`
  padding: 0 ${Spacing.xl}px ${Spacing.xl}px;
  height: 100%;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding-left: ${Spacing.md}px;
    padding-right: ${Spacing.md}px;
  }
`;

const TabNavigation = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
  margin-bottom: ${Spacing.xl}px;
  border-bottom: 2px solid ${Colors.borderLight};
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: ${Spacing.md}px ${Spacing.xl}px;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.active ? Colors.primary : 'transparent'};
  color: ${props => props.active ? Colors.primary : Colors.textSecondary};
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 150ms ease;
  margin-bottom: -2px;
  
  &:hover {
    color: ${Colors.primary};
    background-color: ${Colors.surfaceHover};
  }
`;

const TabContent = styled.div`
  animation: fadeIn 300ms ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${Spacing.lg}px;
  margin-bottom: ${Spacing.xl}px;
`;

const MetricCard = styled.div`
  background-color: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.lg}px;
  box-shadow: ${Shadows.sm};
  transition: all 150ms ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${Shadows.md};
  }
`;

const MetricHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.md}px;
`;

const MetricTitle = styled.h3`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textSecondary};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetricValue = styled.div<{ color?: string }>`
  font-size: ${FontSizes.xxxl}px;
  font-weight: ${FontWeights.bold};
  color: ${props => props.color || Colors.textPrimary};
  margin: ${Spacing.sm}px 0;
`;

const MetricChange = styled.div<{ positive: boolean }>`
  font-size: ${FontSizes.sm}px;
  color: ${props => props.positive ? Colors.success : Colors.danger};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
`;

const ChartContainer = styled.div`
  background-color: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.xl}px;
  box-shadow: ${Shadows.sm};
  margin-bottom: ${Spacing.xl}px;
`;

const ChartTitle = styled.h2`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0 0 ${Spacing.lg}px 0;
`;

const SentimentBar = styled.div`
  display: flex;
  height: 40px;
  border-radius: ${BorderRadius.md}px;
  overflow: hidden;
  margin: ${Spacing.md}px 0;
`;

const SentimentSegment = styled.div<{ width: number; color: string }>`
  width: ${props => props.width}%;
  background-color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Colors.textOnPrimary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  transition: all 150ms ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const SourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${Spacing.md}px;
  margin-top: ${Spacing.xl}px;
`;

const SourceCard = styled.div`
  background-color: ${Colors.backgroundDark};
  border-radius: ${BorderRadius.md}px;
  padding: ${Spacing.md}px;
  text-align: center;
`;

const SourceName = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  margin-bottom: ${Spacing.xs}px;
`;

const SourceScore = styled.div<{ score: number }>`
  font-size: ${FontSizes.xxl}px;
  font-weight: ${FontWeights.bold};
  color: ${props => getSentimentColor(props.score)};
`;

const SourceVolume = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  margin-top: ${Spacing.xs}px;
`;

const TalentTable = styled.table`
  width: 100%;
  background-color: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  overflow: hidden;
  box-shadow: ${Shadows.sm};
`;

const TableHeader = styled.thead`
  background-color: ${Colors.backgroundDark};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${Colors.borderLight};
  transition: all 150ms ease;
  
  &:hover {
    background-color: ${Colors.surfaceHover};
  }
`;

const TableHeaderCell = styled.th`
  padding: ${Spacing.md}px;
  text-align: left;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableCell = styled.td`
  padding: ${Spacing.md}px;
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textPrimary};
`;

const DifficultyBadge = styled.span<{ level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: ${BorderRadius.round}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
  background-color: ${props => getDifficultyColor(props.level)};
  color: ${Colors.textOnPrimary};
`;

const TrendIndicator = styled.span<{ trend: 'increasing' | 'stable' | 'decreasing' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  color: ${props => 
    props.trend === 'increasing' ? Colors.success :
    props.trend === 'decreasing' ? Colors.danger :
    Colors.textSecondary
  };
`;

const CompetitorCard = styled.div`
  background-color: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.lg}px;
  box-shadow: ${Shadows.sm};
  transition: all 150ms ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${Shadows.md};
  }
`;

const CompetitorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.md}px;
`;

const CompetitorName = styled.h3`
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
`;

const CompetitorStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${Spacing.md}px;
  margin-top: ${Spacing.md}px;
`;

const CompetitorStat = styled.div`
  text-align: center;
`;

const StatLabel = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${Spacing.xs}px;
`;

const StatValue = styled.div`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
`;

const RefreshInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.md}px;
  background-color: ${Colors.info}20;
  border-radius: ${BorderRadius.md}px;
  margin-bottom: ${Spacing.xl}px;
`;

const RefreshText = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.info};
`;

const RefreshButton = styled.button`
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background-color: ${Colors.info};
  color: ${Colors.textOnPrimary};
  border: none;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 150ms ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const EventsList = styled.div`
  margin-top: ${Spacing.lg}px;
`;

const EventItem = styled.div`
  padding: ${Spacing.sm}px 0;
  border-bottom: 1px solid ${Colors.borderLight};
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textPrimary};
  
  &:last-child {
    border-bottom: none;
  }
`;

const SkillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs}px;
`;

const SkillTag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background-color: ${Colors.backgroundDark};
  color: ${Colors.textSecondary};
  border-radius: ${BorderRadius.xs}px;
  font-size: ${FontSizes.xs}px;
`;

const MarketIntelligenceScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentRole } = useSelector((state: RootState) => state.user);
  const [activeTab, setActiveTab] = useState<'sentiment' | 'talent' | 'competitors' | 'trends'>('sentiment');
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const marketData = mockMarketIntelligence;
  const canViewDetailedData = hasPermission(currentRole, 'canViewRecruitmentData');
  
  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 2000);
  };
  
  const sentimentTrend = useMemo(() => {
    const recent = marketData.sentimentAnalysis.history.slice(-30);
    const grouped = recent.reduce((acc, item) => {
      if (!acc[item.date]) {
        acc[item.date] = { positive: 0, negative: 0, neutral: 0, count: 0 };
      }
      acc[item.date].positive += item.positive;
      acc[item.date].negative += item.negative;
      acc[item.date].neutral += item.neutral;
      acc[item.date].count++;
      return acc;
    }, {} as Record<string, any>);
    
    return Object.entries(grouped).map(([date, data]) => ({
      date,
      positive: Math.round(data.positive / data.count),
      negative: Math.round(data.negative / data.count),
      neutral: Math.round(data.neutral / data.count)
    }));
  }, [marketData]);
  
  const criticalRoles = marketData.replacementDifficulty.filter(r => r.difficultyLevel === 'CRITICAL');
  
  return (
    <PageContainer>
      <UnifiedPageHeader
        title="Market Intelligence"
        subtitle="External sentiment analysis and talent market insights"
        metadata={{
          timestamp: new Date(marketData.lastRefresh),
          count: marketData.talentAvailability.length,
          status: 'Live'
        }}
        actions={[
          {
            label: 'Export Report',
            onClick: () => console.log('Export clicked'),
            variant: 'secondary',
            icon: '📊'
          },
          {
            label: 'Settings',
            onClick: () => console.log('Settings clicked'),
            variant: 'secondary',
            icon: '⚙️'
          }
        ]}
      />
      
      <ContentArea>
        <RefreshInfo>
        <RefreshText>
          Last updated: {new Date(marketData.lastRefresh).toLocaleString()} | 
          Next refresh: {new Date(marketData.nextScheduledRefresh).toLocaleString()}
        </RefreshText>
        <RefreshButton onClick={handleRefresh}>
          {isRefreshing ? '🔄 Refreshing...' : '🔄 Refresh Now'}
        </RefreshButton>
      </RefreshInfo>
      
      <TabNavigation>
        <TabButton 
          active={activeTab === 'sentiment'} 
          onClick={() => setActiveTab('sentiment')}
        >
          Sentiment Analysis
        </TabButton>
        <TabButton 
          active={activeTab === 'talent'} 
          onClick={() => setActiveTab('talent')}
        >
          Talent Availability
        </TabButton>
        <TabButton 
          active={activeTab === 'competitors'} 
          onClick={() => setActiveTab('competitors')}
        >
          Competitor Activity
        </TabButton>
        <TabButton 
          active={activeTab === 'trends'} 
          onClick={() => setActiveTab('trends')}
        >
          Market Trends
        </TabButton>
      </TabNavigation>
      
      <TabContent>
        {activeTab === 'sentiment' && (
          <>
            <MetricsGrid>
              <MetricCard>
                <MetricHeader>
                  <MetricTitle>Overall Sentiment</MetricTitle>
                </MetricHeader>
                <MetricValue color={getSentimentColor(marketData.sentimentAnalysis.current.overallSentiment)}>
                  {marketData.sentimentAnalysis.current.overallSentiment}%
                </MetricValue>
                <MetricChange positive={marketData.sentimentAnalysis.current.change > 0}>
                  {marketData.sentimentAnalysis.current.change > 0 ? '↑' : '↓'} 
                  {Math.abs(marketData.sentimentAnalysis.current.change)}% vs last week
                </MetricChange>
              </MetricCard>
              
              <MetricCard>
                <MetricHeader>
                  <MetricTitle>Total Mentions</MetricTitle>
                </MetricHeader>
                <MetricValue>
                  {formatNumber(marketData.sentimentAnalysis.current.volume)}
                </MetricValue>
                <MetricChange positive={true}>
                  Across 5 platforms
                </MetricChange>
              </MetricCard>
              
              <MetricCard>
                <MetricHeader>
                  <MetricTitle>Sentiment Accuracy</MetricTitle>
                </MetricHeader>
                <MetricValue color={Colors.success}>
                  84%
                </MetricValue>
                <MetricChange positive={true}>
                  Validated against human coding
                </MetricChange>
              </MetricCard>
            </MetricsGrid>
            
            <ChartContainer>
              <ChartTitle>Sentiment Distribution (Last 30 Days)</ChartTitle>
              <SentimentBar>
                <SentimentSegment width={55} color={Colors.success}>
                  Positive 55%
                </SentimentSegment>
                <SentimentSegment width={32} color={Colors.warning}>
                  Neutral 32%
                </SentimentSegment>
                <SentimentSegment width={13} color={Colors.danger}>
                  Negative 13%
                </SentimentSegment>
              </SentimentBar>
              
              <SourceGrid>
                {marketData.sentimentAnalysis.sources.map(source => (
                  <SourceCard key={source.source}>
                    <SourceName>{source.source}</SourceName>
                    <SourceScore score={source.sentiment}>{source.sentiment}%</SourceScore>
                    <SourceVolume>{formatNumber(source.volume)} mentions</SourceVolume>
                  </SourceCard>
                ))}
              </SourceGrid>
            </ChartContainer>
            
            <ChartContainer>
              <ChartTitle>Correlated Events</ChartTitle>
              <EventsList>
                {marketData.sentimentAnalysis.current.correlatedEvents?.map((event, index) => (
                  <EventItem key={index}>{event}</EventItem>
                ))}
              </EventsList>
            </ChartContainer>
            
            <ChartContainer>
              <ChartTitle>Top Mentions</ChartTitle>
              <SkillsContainer>
                {marketData.sentimentAnalysis.current.topMentions.map(mention => (
                  <SkillTag key={mention} style={{ 
                    padding: '8px 16px', 
                    fontSize: `${FontSizes.sm}px`,
                    backgroundColor: Colors.primary + '20',
                    color: Colors.primary
                  }}>
                    {mention}
                  </SkillTag>
                ))}
              </SkillsContainer>
            </ChartContainer>
          </>
        )}
        
        {activeTab === 'talent' && canViewDetailedData && (
          <>
            {criticalRoles.length > 0 && (
              <ChartContainer style={{ backgroundColor: Colors.danger + '10', marginBottom: `${Spacing.xl}px` }}>
                <ChartTitle style={{ color: Colors.danger }}>
                  ⚠️ Critical Replacement Difficulty Alert
                </ChartTitle>
                <div style={{ color: Colors.textPrimary, fontSize: `${FontSizes.sm}px` }}>
                  {criticalRoles.length} role(s) identified as CRITICAL to replace:
                </div>
                {criticalRoles.map(role => (
                  <div key={role.role} style={{ 
                    marginTop: `${Spacing.md}px`,
                    padding: `${Spacing.md}px`,
                    backgroundColor: Colors.surface,
                    borderRadius: `${BorderRadius.md}px`
                  }}>
                    <strong>{role.role}</strong> - Only {role.marketAvailability} candidates available
                    <div style={{ fontSize: `${FontSizes.xs}px`, color: Colors.textSecondary, marginTop: `${Spacing.xs}px` }}>
                      Estimated {role.estimatedTimeToFill} days to fill | €{role.estimatedCostToReplace.toLocaleString()} replacement cost
                    </div>
                  </div>
                ))}
              </ChartContainer>
            )}
            
            <TalentTable>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Role</TableHeaderCell>
                  <TableHeaderCell>Location</TableHeaderCell>
                  <TableHeaderCell>Total Pool</TableHeaderCell>
                  <TableHeaderCell>Active</TableHeaderCell>
                  <TableHeaderCell>Avg Salary</TableHeaderCell>
                  <TableHeaderCell>Difficulty</TableHeaderCell>
                  <TableHeaderCell>Trend</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <tbody>
                {marketData.talentAvailability.map(talent => (
                  <TableRow key={`${talent.role}-${talent.location}`}>
                    <TableCell>
                      <strong>{talent.role}</strong>
                    </TableCell>
                    <TableCell>{talent.location}</TableCell>
                    <TableCell>{formatNumber(talent.totalCandidates)}</TableCell>
                    <TableCell>{talent.activelyLooking}</TableCell>
                    <TableCell>€{talent.averageSalary.toLocaleString()}</TableCell>
                    <TableCell>
                      <DifficultyBadge level={talent.difficultyIndex}>
                        {talent.difficultyIndex}
                      </DifficultyBadge>
                    </TableCell>
                    <TableCell>
                      <TrendIndicator trend={talent.trend}>
                        {talent.trend === 'increasing' && '↑'}
                        {talent.trend === 'stable' && '→'}
                        {talent.trend === 'decreasing' && '↓'}
                        {talent.trend}
                      </TrendIndicator>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </TalentTable>
            
            <ChartContainer style={{ marginTop: `${Spacing.xl}px` }}>
              <ChartTitle>Replacement Difficulty Analysis</ChartTitle>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: `${Spacing.lg}px` }}>
                {marketData.replacementDifficulty.map(item => (
                  <div key={item.role} style={{
                    padding: `${Spacing.md}px`,
                    backgroundColor: Colors.backgroundDark,
                    borderRadius: `${BorderRadius.md}px`,
                    borderLeft: `4px solid ${getDifficultyColor(item.difficultyLevel)}`
                  }}>
                    <div style={{ marginBottom: `${Spacing.sm}px` }}>
                      <strong>{item.role}</strong>
                      <DifficultyBadge level={item.difficultyLevel} style={{ marginLeft: `${Spacing.sm}px` }}>
                        {item.difficultyLevel}
                      </DifficultyBadge>
                    </div>
                    <div style={{ fontSize: `${FontSizes.sm}px`, color: Colors.textSecondary }}>
                      <div>Score: {item.difficultyScore}/100</div>
                      <div>Time to Fill: {item.estimatedTimeToFill} days</div>
                      <div>Cost: €{item.estimatedCostToReplace.toLocaleString()}</div>
                      {item.attritionRisk && (
                        <div style={{ color: item.attritionRisk > 40 ? Colors.warning : Colors.textSecondary }}>
                          Attrition Risk: {item.attritionRisk}%
                        </div>
                      )}
                    </div>
                    <div style={{ marginTop: `${Spacing.sm}px` }}>
                      <strong style={{ fontSize: `${FontSizes.xs}px` }}>Recommendations:</strong>
                      <ul style={{ margin: `${Spacing.xs}px 0`, paddingLeft: '20px', fontSize: `${FontSizes.xs}px` }}>
                        {item.recommendations.slice(0, 2).map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </ChartContainer>
          </>
        )}
        
        {activeTab === 'competitors' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: `${Spacing.lg}px` }}>
            {marketData.competitorActivity.map(competitor => (
              <CompetitorCard key={competitor.company}>
                <CompetitorHeader>
                  <CompetitorName>{competitor.company}</CompetitorName>
                  <TrendIndicator trend={competitor.trend}>
                    {competitor.trend === 'increasing' && '↑'}
                    {competitor.trend === 'stable' && '→'}
                    {competitor.trend === 'decreasing' && '↓'}
                  </TrendIndicator>
                </CompetitorHeader>
                
                <CompetitorStats>
                  <CompetitorStat>
                    <StatLabel>Hiring Volume</StatLabel>
                    <StatValue>{competitor.hiringVolume}</StatValue>
                  </CompetitorStat>
                  <CompetitorStat>
                    <StatLabel>Open Roles</StatLabel>
                    <StatValue>{competitor.openRoles}</StatValue>
                  </CompetitorStat>
                  <CompetitorStat>
                    <StatLabel>Avg Salary</StatLabel>
                    <StatValue>€{(competitor.averageSalary / 1000).toFixed(0)}k</StatValue>
                  </CompetitorStat>
                </CompetitorStats>
                
                <div style={{ marginTop: `${Spacing.md}px` }}>
                  <div style={{ fontSize: `${FontSizes.xs}px`, color: Colors.textSecondary, marginBottom: `${Spacing.xs}px` }}>
                    Top Hiring Roles:
                  </div>
                  <SkillsContainer>
                    {competitor.topRoles.map(role => (
                      <SkillTag key={role}>{role}</SkillTag>
                    ))}
                  </SkillsContainer>
                </div>
              </CompetitorCard>
            ))}
          </div>
        )}
        
        {activeTab === 'trends' && (
          <>
            <ChartContainer>
              <ChartTitle>Hot Skills in Market</ChartTitle>
              <SkillsContainer>
                {marketData.marketTrends.hotSkills.map(skill => (
                  <SkillTag key={skill} style={{
                    padding: '12px 20px',
                    fontSize: `${FontSizes.sm}px`,
                    backgroundColor: Colors.success + '20',
                    color: Colors.success,
                    fontWeight: FontWeights.medium
                  }}>
                    🔥 {skill}
                  </SkillTag>
                ))}
              </SkillsContainer>
            </ChartContainer>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: `${Spacing.lg}px` }}>
              <ChartContainer>
                <ChartTitle>📈 Emerging Roles</ChartTitle>
                {marketData.marketTrends.emergingRoles.map(role => (
                  <div key={role} style={{
                    padding: `${Spacing.sm}px`,
                    borderBottom: `1px solid ${Colors.borderLight}`,
                    fontSize: `${FontSizes.sm}px`,
                    color: Colors.success
                  }}>
                    {role}
                  </div>
                ))}
              </ChartContainer>
              
              <ChartContainer>
                <ChartTitle>📉 Declining Roles</ChartTitle>
                {marketData.marketTrends.decliningRoles.map(role => (
                  <div key={role} style={{
                    padding: `${Spacing.sm}px`,
                    borderBottom: `1px solid ${Colors.borderLight}`,
                    fontSize: `${FontSizes.sm}px`,
                    color: Colors.danger
                  }}>
                    {role}
                  </div>
                ))}
              </ChartContainer>
            </div>
            
            <ChartContainer>
              <ChartTitle>Salary Trends (YoY Change)</ChartTitle>
              <div style={{ display: 'grid', gap: `${Spacing.sm}px` }}>
                {marketData.marketTrends.salaryTrends.map(trend => (
                  <div key={trend.role} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: `${Spacing.md}px`,
                    backgroundColor: Colors.backgroundDark,
                    borderRadius: `${BorderRadius.sm}px`
                  }}>
                    <span>{trend.role}</span>
                    <span style={{
                      color: trend.change > 10 ? Colors.success : 
                             trend.change > 5 ? Colors.warning : 
                             Colors.textSecondary,
                      fontWeight: FontWeights.semibold
                    }}>
                      +{trend.change}%
                    </span>
                  </div>
                ))}
              </div>
            </ChartContainer>
          </>
        )}
      </TabContent>
      </ContentArea>
    </PageContainer>
  );
};

export default MarketIntelligenceScreen;